import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    X,
    Trash2,
    Eye,
    Edit2,
    Building2,
    Mail,
    Link as LinkIcon,
    Search,
    Upload,
} from "lucide-react";
import "./Partenaireacademique.css";
import { API_BASE_URL } from "../../../../../../api";
import {
    fetchPartenairesAcadRequest,
    createPartenaireAcadRequest,
    updatePartenaireAcadRequest,
    deletePartenaireAcadRequest,
} from "../../../../../redux/actions/partenairesAcademiquesActions"; // ✅ adapte si besoin

const Modal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="paModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="paModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="paModal__header">
                    <div className="paModal__title">{title}</div>
                    <button className="paIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>
                <div className="paModal__body">{children}</div>
                {footer && <div className="paModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default function PartenaireAcademique() {
    const dispatch = useDispatch();
    const { partenairesAcademiques: partenaires, loading } = useSelector((s) => s.partenairesAcademiques);


    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedPartenaire, setSelectedPartenaire] = useState(null);

    const [form, setForm] = useState({
        nom: "",
        description: "",
        email: "",
        url: "",
        imageFile: null,
        iconPreview: null,
    });

    const [filters, setFilters] = useState({ nom: "", email: "" });

    useEffect(() => {
        dispatch(fetchPartenairesAcadRequest());
    }, [dispatch]);

    const filteredPartenaires = useMemo(() => {
        let data = [...(partenaires || [])];
        if (filters.nom) data = data.filter((p) => (p.nom || "").toLowerCase().includes(filters.nom.toLowerCase()));
        if (filters.email) data = data.filter((p) => (p.email || "").toLowerCase().includes(filters.email.toLowerCase()));
        return data;
    }, [partenaires, filters]);

    const resetForm = () => {
        setForm({
            nom: "",
            description: "",
            email: "",
            url: "",
            imageFile: null,
            iconPreview: null,
        });
    };

    const openCreate = () => {
        resetForm();
        setShowAddModal(true);
    };
    const closeCreate = () => {
        setShowAddModal(false);
        resetForm();
    };

    const openEdit = (p) => {
        setSelectedPartenaire(p);
        setForm({
            nom: p?.nom || "",
            description: p?.description || "",
            email: p?.email || "",
            url: p?.siteweb || "",
            imageFile: null,
            iconPreview: null,
        });
        setShowEditModal(true);
    };
    const closeEdit = () => {
        setShowEditModal(false);
        setSelectedPartenaire(null);
        resetForm();
    };

    const openView = (p) => {
        setSelectedPartenaire(p);
        setShowViewModal(true);
    };
    const closeView = () => {
        setShowViewModal(false);
        setSelectedPartenaire(null);
    };

    const openDelete = (p) => {
        setSelectedPartenaire(p);
        setShowDeleteModal(true);
    };
    const closeDelete = () => {
        setShowDeleteModal(false);
        setSelectedPartenaire(null);
    };

    const handleFilterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));
    const resetFilters = () => setFilters({ nom: "", email: "" });
    const isAnyFilterFilled = filters.nom || filters.email;

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setForm((prev) => ({
                ...prev,
                imageFile: file,
                iconPreview: reader.result,
            }));
        };
        reader.readAsDataURL(file);
    };

    const validateForm = () => {
        if (!form.nom.trim() || !form.email.trim()) {
            alert("Nom et email obligatoires");
            return false;
        }
        return true;
    };

    const submitCreate = () => {
        if (!validateForm()) return;

        dispatch(
            createPartenaireAcadRequest({
                nom: form.nom,
                description: form.description,
                email: form.email,
                siteweb: form.url,
                imageFile: form.imageFile,
            })
        );

        closeCreate();
    };

    const submitEdit = () => {
        if (!validateForm()) return;

        dispatch(
            updatePartenaireAcadRequest(selectedPartenaire.id, {
                nom: form.nom,
                description: form.description,
                email: form.email,
                siteweb: form.url,
                imageFile: form.imageFile,
            })
        );

        closeEdit();
    };

    const confirmDelete = () => {
        dispatch(deletePartenaireAcadRequest(selectedPartenaire?.id));
        closeDelete();
    };

    const getImageUrl = (picture) => `${API_BASE_URL}/api/partenaires-academiques/images/${picture}`;

    return (
        <div className="paPage">
            <div className="paHeader">
                <div>
                    <h1>Partenaires Académiques</h1>
                    <p>Gérez vos partenaires universitaires et établissements d'enseignement</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="paWrap">
                <div className="paCard">
                    <div className="paBody">
                        <div className="paFilterSection">
                            <div className="paFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="paFilterGrid">
                                <div className="paField">
                                    <label>Nom</label>
                                    <input
                                        type="text"
                                        value={filters.nom}
                                        onChange={(e) => handleFilterChange("nom", e.target.value)}
                                        placeholder="Rechercher par nom..."
                                    />
                                </div>

                                <div className="paField">
                                    <label>Email</label>
                                    <input
                                        type="text"
                                        value={filters.email}
                                        onChange={(e) => handleFilterChange("email", e.target.value)}
                                        placeholder="Rechercher par email..."
                                    />
                                </div>
                            </div>

                            <div className="paFilterActions">
                                <button className="paBtn paBtn--primary" type="button" onClick={openCreate} disabled={loading}>
                                    <Plus size={16} />
                                    Ajouter un partenaire académique
                                </button>

                                <button
                                    className={`paBtn ${isAnyFilterFilled ? "paBtn--warning" : "paBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Liste */}
            <div className="paWrap">
                <div className="paCard">
                    <div className="paBody">
                        <div className="paTopRow">
                            <h2 className="paTitle">Liste des partenaires académiques ({filteredPartenaires.length})</h2>
                        </div>

                        <div className="paTableWrap">
                            <table className="paTable">
                                <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th>Email</th>
                                    <th>Site Web</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredPartenaires.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="paEmptyCell">
                                            Aucun partenaire académique trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPartenaires.map((p) => (
                                        <tr key={p.id}>
                                            <td>
                                                {p.picture ? (
                                                    <div className="paImagePreview" style={{ width: 44, height: 44 }}>
                                                        <img
                                                            src={getImageUrl(p.picture)}
                                                            alt="partenaire"
                                                            style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "10px" }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="paImagePlaceholder">
                                                        <Building2 size={24} />
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                          <span className="paInline">
                            <span className="paStrong">{p.nom}</span>
                          </span>
                                            </td>

                                            <td className="paMuted">{p.description || "—"}</td>

                                            <td>
                                                <a href={`mailto:${p.email}`} className="paLink">
                                                    <Mail size={14} />
                                                    {p.email}
                                                </a>
                                            </td>

                                            <td>
                                                {p.siteweb ? (
                                                    <a href={p.siteweb} target="_blank" rel="noopener noreferrer" className="paLink">
                                                        <LinkIcon size={14} />
                                                        Visiter
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>

                                            <td className="is-right">
                                                <div className="paRowActions">
                                                    <button className="paIconBtn" type="button" title="Voir" onClick={() => openView(p)}>
                                                        <Eye size={16} />
                                                    </button>
                                                    <button className="paIconBtn" type="button" title="Modifier" onClick={() => openEdit(p)}>
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="paIconBtn paIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => openDelete(p)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Ajout */}
            <Modal
                open={showAddModal}
                title="Nouveau partenaire académique"
                onClose={closeCreate}
                footer={
                    <>
                        <button className="paBtn paBtn--ghost" type="button" onClick={closeCreate}>
                            Annuler
                        </button>
                        <button className="paBtn paBtn--primary" type="button" onClick={submitCreate}>
                            Créer
                        </button>
                    </>
                }
            >
                <div className="paForm">
                    <div className="paField">
                        <label>Nom *</label>
                        <input
                            type="text"
                            value={form.nom}
                            onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                            placeholder="Ex: Université Mohammed V"
                        />
                    </div>

                    <div className="paField">
                        <label>Description</label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Description du partenariat..."
                        />
                    </div>

                    <div className="paField">
                        <label>Email *</label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder="contact@universite.ma"
                        />
                    </div>

                    <div className="paField">
                        <label>Site Web</label>
                        <input
                            type="url"
                            value={form.url}
                            onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                            placeholder="https://www.universite.ma"
                        />
                    </div>

                    <div className="paField">
                        <label>Image</label>
                        <div className="paFileUpload">
                            <input
                                type="file"
                                id="icon-upload"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <label htmlFor="icon-upload" className="paFileUploadBtn">
                                <Upload size={16} />
                                Choisir un fichier
                            </label>

                            {form.iconPreview && (
                                <div className="paImagePreview">
                                    <img src={form.iconPreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="paRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, iconPreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="paFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Edit */}
            <Modal
                open={showEditModal}
                title={`Modifier — ${selectedPartenaire?.nom || ""}`}
                onClose={closeEdit}
                footer={
                    <>
                        <button className="paBtn paBtn--ghost" type="button" onClick={closeEdit}>
                            Annuler
                        </button>
                        <button className="paBtn paBtn--primary" type="button" onClick={submitEdit}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="paForm">
                    <div className="paField">
                        <label>Nom *</label>
                        <input type="text" value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))} />
                    </div>

                    <div className="paField">
                        <label>Description</label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        />
                    </div>

                    <div className="paField">
                        <label>Email *</label>
                        <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                    </div>

                    <div className="paField">
                        <label>Site Web</label>
                        <input type="url" value={form.url} onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))} />
                    </div>

                    <div className="paField">
                        <label>Image</label>
                        <div className="paFileUpload">
                            <input
                                type="file"
                                id="icon-upload-edit"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <label htmlFor="icon-upload-edit" className="paFileUploadBtn">
                                <Upload size={16} />
                                Choisir un fichier
                            </label>

                            {form.iconPreview && (
                                <div className="paImagePreview">
                                    <img src={form.iconPreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="paRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, iconPreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="paFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal View */}
            <Modal
                open={showViewModal}
                title={`Détails — ${selectedPartenaire?.nom || ""}`}
                onClose={closeView}
                footer={
                    <button className="paBtn paBtn--primary" type="button" onClick={closeView}>
                        Fermer
                    </button>
                }
            >
                {selectedPartenaire && (
                    <div className="paViewContent">
                        <div className="paViewRow">
                            <div className="paViewLabel">Nom:</div>
                            <div className="paViewValue">{selectedPartenaire.nom}</div>
                        </div>

                        <div className="paViewRow">
                            <div className="paViewLabel">Description:</div>
                            <div className="paViewValue">{selectedPartenaire.description || "—"}</div>
                        </div>

                        <div className="paViewRow">
                            <div className="paViewLabel">Email:</div>
                            <div className="paViewValue">
                                <a href={`mailto:${selectedPartenaire.email}`} className="paLink">
                                    {selectedPartenaire.email}
                                </a>
                            </div>
                        </div>

                        <div className="paViewRow">
                            <div className="paViewLabel">Site Web:</div>
                            <div className="paViewValue">
                                {selectedPartenaire.siteweb ? (
                                    <a href={selectedPartenaire.siteweb} target="_blank" rel="noopener noreferrer" className="paLink">
                                        {selectedPartenaire.siteweb}
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal Delete */}
            <Modal
                open={showDeleteModal}
                title="Confirmation de suppression"
                onClose={closeDelete}
                footer={
                    <>
                        <button className="paBtn paBtn--ghost" type="button" onClick={closeDelete}>
                            Annuler
                        </button>
                        <button className="paBtn paBtn--danger" type="button" onClick={confirmDelete}>
                            Supprimer
                        </button>
                    </>
                }
            >
                <div className="paDeleteContent">
                    <p>
                        Voulez-vous vraiment supprimer le partenaire académique <strong>{selectedPartenaire?.nom}</strong> ?
                    </p>
                    <p className="paWarning">Cette action est irréversible.</p>
                </div>
            </Modal>
        </div>
    );
}
